import { useFetch } from "hooks/api";
import useDefaultState from "hooks/useDefaultState";
import { Col, Row } from "react-bootstrap";
import { Button } from "components/core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import BudgetProgramLog from "components/custom/programs/budget/BudgetProgramLog";
import SelectField from "components/core/basic/SelectField";
import { successToast, errorToast } from "../../utils/toasts";

const RulesSection = ({ advertisement }) => {
  const queryClient = useQueryClient();
  const { data: programsData, isLoading: isProgramDataLoading } = useFetch(
    ["programs"],
    "/programs/",
    {
      engine: "budget",
    },
    { select: (data) => data?.data?.results ?? [] },
  );

  const { data: adPlanData, isLoading: isAdPlanDataLoading } = useFetch(
    ["media-plan", advertisement.id],
    "/api/gvads/advproduct/",
    {
      advertisement: advertisement.id,
    },
    {
      select: (d) => d.data,
      enabled: !!advertisement?.id,
    },
  );
  const adPlanParams = useMemo(() => {
    return adPlanData?.ad_control_fields ?? {};
  }, [adPlanData]);

  const [budgetProgram, setBudgetProgram] = useDefaultState(adPlanParams.budget_program || -1);

  const updateProgram = useMutation({
    mutationFn: async (program) => {
      await api.put(`/api/gvads/advproduct/`, {
        id: advertisement.id,
        ad_control_fields: {
          budget_program: program === -1 ? null : program,
        },
        state: advertisement.state,
        custom_api_request: "update_advertisement",
      });
    },

    onSuccess: () => {
      successToast({ message: "Updating Media Plan" });
      queryClient.invalidateQueries({
        queryKey: ["media-plan", advertisement.id],
      });
    },

    onError: (error) => {
      errorToast({ message: `Failed to Update Media Plan`, error });
    },
  });

  const programOptions =
    programsData?.map((program) => {
      return { value: program.id, label: program.name };
    }) ?? [];
  programOptions.unshift({
    value: -1,
    label: "No Media Plan Rules",
  });

  const isDirty = budgetProgram !== (adPlanParams.budget_program || -1);

  return (
    <section>
      <h5 className="fw-bold pb-4 pt-1">Media Plan Rules</h5>
      <Row>
        <Col xs={12} lg={6} className="d-flex align-items-center">
          <div className="flex-fill pe-3">
            <SelectField
              value={budgetProgram}
              onChange={setBudgetProgram}
              options={programOptions}
              isLoading={!programOptions || isProgramDataLoading}
            />
          </div>
          <Button
            onPress={() => {
              updateProgram.mutate(budgetProgram);
            }}
            isDisabled={
              !isDirty || updateProgram.isPending || isAdPlanDataLoading || advertisement.serving_status === "ENDED"
            }
          >
            Save
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={6}>
          <p className="fs-5 mt-3">
            Use Media Plan Rules to control the budget for this advertisement based on performance. Click here to create
            a new <Link to="/user/advertising/settings">Media Plan Rules</Link> .
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <BudgetProgramLog advertisementId={advertisement.id} />
        </Col>
      </Row>
    </section>
  );
};

export default RulesSection;
